import type { OrderDocument } from '../types/orderTypes'

// Rules shared by the receipt and the title deed, kept pure so they are
// testable and identical on every page that shows either document.

const TITLE_DEED_DOCUMENT_TYPE = 'dldtokencertificate'
const UNSAFE_REFERENCE_CHARS = /[^a-zA-Z0-9-]/g
const DEFAULT_REFERENCE = 'order'

/** orderReference made safe for a file name; "order" when nothing survives. */
export const toFileNameReference = (orderReference: string | null | undefined): string => {
  const safe = (orderReference ?? '').replace(UNSAFE_REFERENCE_CHARS, '-')
  return /[a-zA-Z0-9]/.test(safe) ? safe : DEFAULT_REFERENCE
}

export const fallbackReceiptFileName = (orderReference: string | null | undefined): string =>
  `Sliceal_Order_Receipt_${toFileNameReference(orderReference)}.pdf`

export const fallbackTitleDeedFileName = (orderReference: string | null | undefined): string =>
  `Sliceal_Title_Deed_Certificate_${toFileNameReference(orderReference)}.pdf`

/**
 * The title deed: the FIRST DLD token certificate row with a link. The list
 * is newest first, so a re-issued certificate wins over the one it replaces.
 */
export const findTitleDeed = (documents: readonly OrderDocument[]): OrderDocument | null =>
  documents.find(
    (doc) => doc.type.toLowerCase() === TITLE_DEED_DOCUMENT_TYPE && doc.url.length > 0,
  ) ?? null

export interface TitleDeedPollInput {
  documents: readonly OrderDocument[] | undefined
  /** False for closed orders and pages that never poll (holding). */
  shouldPoll: boolean
  /** Fixed once at mount — `Date.now() + window`. */
  deadline: number
  intervalMs: number
  now?: number
}

/**
 * After each documents response: stop once the certificate is in, or once
 * the window has run out; otherwise read again in `intervalMs`.
 */
export const titleDeedPollDelay = ({
  documents,
  shouldPoll,
  deadline,
  intervalMs,
  now = Date.now(),
}: TitleDeedPollInput): number | false => {
  if (!shouldPoll) return false
  if (documents && findTitleDeed(documents)) return false
  return now < deadline ? intervalMs : false
}
